/**
 * 用語集機能が対応する言語の定義。
 * `code` はサーバーに渡す言語コード（CSV の言語列にも使用）。
 * `i18nKey` は UI に言語名を表示するときのキー（UI 言語に合わせて表示名が変わる）。
 *              vocab API に送る言語コードとしても兼用する。
 * `script` は sounds_like の文字種バリデーションに使う文字体系。
 *   Speechmatics のドキュメント:
 *   "sounds_like only works with the main script for that language"
 *   "Japanese (ja) sounds_like only supports full width Hiragana or Katakana"
 *   参照: https://docs.speechmatics.com/speech-to-text/features/custom-dictionary
 */

/**
 * sounds_like の文字種バリデーションで使う文字体系の識別子。
 * Unicode スクリプトプロパティ（\p{sc=...}）に対応する。
 */
export type GlossaryScript =
  | 'arabic'
  | 'bengali'
  | 'cyrillic'
  | 'devanagari'
  | 'greek'
  | 'han'
  | 'hangul'
  | 'hebrew'
  | 'japanese'
  | 'latin'
  | 'tamil'
  | 'thai';

export type GlossaryLanguage = {
  code: string;
  i18nKey: string;
  /** sounds_like の文字種バリデーションに使う文字体系 */
  script: GlossaryScript;
};

/**
 * 用語集機能が対応する言語の一覧。
 *
 * この一覧はサーバ側の STT エンジン定義（pocketalk_language_code_stt_engine_doutsu_dev.json）で
 * SM_ENHANCED が指定されている言語と一致させる。
 * エンジン割り当てが変わった場合はここも更新が必要。
 * ※ サーバ側定義とフロントの配列が二重管理になっているため、変更時は両方確認すること。
 */
export const GLOSSARY_LANGUAGES: readonly GlossaryLanguage[] = [
  { code: 'arb', i18nKey: 'ar', script: 'arabic' },
  { code: 'bul', i18nKey: 'bg', script: 'cyrillic' },
  { code: 'ben', i18nKey: 'bn', script: 'bengali' },
  { code: 'cat', i18nKey: 'ca', script: 'latin' },
  { code: 'ces', i18nKey: 'cs', script: 'latin' },
  { code: 'deu', i18nKey: 'de', script: 'latin' },
  { code: 'ell', i18nKey: 'el', script: 'greek' },
  { code: 'eng-US', i18nKey: 'en', script: 'latin' },
  { code: 'spa-ES', i18nKey: 'es', script: 'latin' },
  { code: 'fas', i18nKey: 'fa', script: 'arabic' },
  { code: 'fin', i18nKey: 'fi', script: 'latin' },
  { code: 'fra-FR', i18nKey: 'fr', script: 'latin' },
  { code: 'glg', i18nKey: 'gl', script: 'latin' },
  { code: 'hin', i18nKey: 'hi', script: 'devanagari' },
  { code: 'hrv', i18nKey: 'hr', script: 'latin' },
  { code: 'hun', i18nKey: 'hu', script: 'latin' },
  { code: 'ind', i18nKey: 'id', script: 'latin' },
  { code: 'ita', i18nKey: 'it', script: 'latin' },
  { code: 'heb', i18nKey: 'iw', script: 'hebrew' },
  { code: 'jpn', i18nKey: 'ja', script: 'japanese' },
  { code: 'kor', i18nKey: 'ko', script: 'hangul' },
  { code: 'lit', i18nKey: 'lt', script: 'latin' },
  { code: 'lav', i18nKey: 'lv', script: 'latin' },
  { code: 'mon', i18nKey: 'mn', script: 'cyrillic' },
  { code: 'msa', i18nKey: 'ms', script: 'latin' },
  { code: 'nld', i18nKey: 'nl', script: 'latin' },
  { code: 'nor', i18nKey: 'no', script: 'latin' },
  { code: 'pol', i18nKey: 'pl', script: 'latin' },
  { code: 'por-PT', i18nKey: 'pt', script: 'latin' },
  { code: 'ron', i18nKey: 'ro', script: 'latin' },
  { code: 'rus', i18nKey: 'ru', script: 'cyrillic' },
  { code: 'slk', i18nKey: 'sk', script: 'latin' },
  { code: 'slv', i18nKey: 'sl', script: 'latin' },
  { code: 'swe', i18nKey: 'sv', script: 'latin' },
  { code: 'swa', i18nKey: 'sw', script: 'latin' },
  { code: 'tam', i18nKey: 'ta', script: 'tamil' },
  { code: 'tha', i18nKey: 'th', script: 'thai' },
  { code: 'fil', i18nKey: 'tl', script: 'latin' },
  { code: 'tur', i18nKey: 'tr', script: 'latin' },
  { code: 'ukr', i18nKey: 'uk', script: 'cyrillic' },
  { code: 'vie', i18nKey: 'vi', script: 'latin' },
  { code: 'yue', i18nKey: 'yue', script: 'han' },
  { code: 'zho-Hans-CN', i18nKey: 'zh-CN', script: 'han' },
  { code: 'zho-Hant-TW', i18nKey: 'zh-TW', script: 'han' },
];

/** 言語コード → i18n キーの高速ルックアップ用マップ。 */
const CODE_TO_I18N_KEY = new Map(
  GLOSSARY_LANGUAGES.map((l) => [l.code, l.i18nKey]),
);

/**
 * 言語コードに対応する i18n キーを返す。
 * 未知のコードはそのまま返す（フォールバック）。
 */
export const getGlossaryLanguageI18nKey = (code: string): string =>
  CODE_TO_I18N_KEY.get(code) ?? code;

/**
 * CSV バリデーションで使用する対応言語コードのセット。
 * `GLOSSARY_LANGUAGES` から自動生成する。
 */
export const SUPPORTED_LANGUAGE_CODES = new Set(
  GLOSSARY_LANGUAGES.map((l) => l.code),
);

/**
 * 言語コード → GlossaryScript のルックアップ用マップ。
 * csvParser の sounds_like 文字種バリデーションで使用する。
 */
export const SCRIPT_BY_LANGUAGE = new Map<string, GlossaryScript>(
  GLOSSARY_LANGUAGES.map((l) => [l.code, l.script]),
);
